import { ComponentPortal } from '@angular/cdk/portal';
import { Type, ComponentRef, Injector } from '@angular/core';

import { GoogleMap } from '@lib/geo/google/map';
import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';

import { Evented } from '../core';
import { toGoogleMapLatLng } from '../helpers';
import { GOOGLE_MAP_POPUP_DATA } from '../providers';
import { IGoogleMapPopupEvents } from './popup-events';
import { IGoogleMapPopupOptions } from './popup-options';

const DEFAULT_OPTIONS: IGoogleMapPopupOptions = {
  disableAutoPan: false,
};

export class GoogleMapPopup extends Evented<IGoogleMapPopupEvents> {
  private _options: IGoogleMapPopupOptions;

  private readonly _component: Type<any>;
  private _element: HTMLElement;
  private _componentRef: ComponentRef<any>;

  private _map: GoogleMap;
  private _opened: boolean = false;
  private readonly _googleInfoWindow: google.maps.InfoWindow;

  constructor(component: Type<any>, options?: IGoogleMapPopupOptions) {
    super();

    this._options = { ...DEFAULT_OPTIONS, ...options };

    this._component = component;
    this._googleInfoWindow = this._createGoogleInfoWindow();

    if (this._options.lonLat) {
      this._updatePosition();
    }
  }

  get componentInstance(): any {
    return this._componentRef.instance;
  }

  get id(): string {
    return this._options?.id;
  }

  public isOpen(): boolean {
    return this._opened;
  }

  public getLonLat(): SnLonLat {
    return SnLonLat.convert(this._options.lonLat);
  }

  public setLonLat(lonLat: SnLonLatLike): this {
    this._updateOptions({ lonLat });
    this._updatePosition();

    this.fire('lonLatChange');

    return this;
  }

  public setContent(data: any): void {
    this._options.data = data;
    this._updateOptions(this._options);
  }

  public open(map: GoogleMap): this {
    if (this._opened) {
      return;
    }

    this._map = map;

    this._map.zone.run(() => {
      this._initPopupComponent();
    });

    this._googleInfoWindow.setContent(this._element);
    this._googleInfoWindow.open(map.mapInstance);

    this._opened = true;

    this.fire('open');

    return this;
  }

  public close(): this {
    if (!this._opened) {
      return;
    }
    this._destroyPopupComponent();
    this._googleInfoWindow.setContent(null);
    this._googleInfoWindow.close();

    this._opened = false;

    this.fire('close');

    return this;
  }

  private _updatePosition(): void {
    const lonLat = SnLonLat.convert(this._options.lonLat);
    this._googleInfoWindow.setPosition(toGoogleMapLatLng(lonLat));
  }

  private _updateOptions(options: IGoogleMapPopupOptions): this {
    this._options = { ...this._options, ...options };
    return this;
  }

  private _initPopupComponent(): void {
    const parentInjector = this._options.viewContainerRef
      ? this._options.viewContainerRef.injector
      : this._map.injector;
    const viewContainerRef = this._options.viewContainerRef
      ? this._options.viewContainerRef
      : this._map.viewContainerRef;

    const componentInjector = Injector.create({
      providers: [{ provide: GOOGLE_MAP_POPUP_DATA, useValue: this._options.data }],
      parent: parentInjector,
    });

    const componentPortal = new ComponentPortal(
      this._component,
      viewContainerRef,
      componentInjector,
    );

    this._componentRef = this._map.portal.attachComponentPortal(componentPortal);
    this._element = this._componentRef.location.nativeElement;
    if (this._componentRef.instance.close$) {
      this._componentRef.instance.close$.subscribe(() => {
        this.close();
      });
    }
  }

  private _destroyPopupComponent(): void {
    this._componentRef.destroy();

    delete this._componentRef;
    delete this._element;
  }

  private _createGoogleInfoWindow(): google.maps.InfoWindow {
    const googleInfoWindow = new google.maps.InfoWindow({
      disableAutoPan: this._options.disableAutoPan,
      pixelOffset: this._options.pixelOffset,
    });
    googleInfoWindow.addListener('closeclick', this.close.bind(this));

    return googleInfoWindow;
  }
}
